// Helpers
const pad2 = (n) => String(n).padStart(2, "0");

/** Parse YYYY-MM-DD as local date (evita desvio UTC no dia da semana). */
export const parseLocalDate = (iso) => {
  if (!iso) return null;
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const fmtDate = (iso) => {
  const d = parseLocalDate(iso);
  if (!d) return "";
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

export const fmtWeekday = (iso) => {
  const d = parseLocalDate(iso);
  if (!d) return "";
  try {
    return new Intl.DateTimeFormat("pt-PT", { weekday: "long" }).format(d);
  } catch {
    return "";
  }
};

export const fmtTime = (time) => (time ? String(time).slice(0, 5) : "");

const WEEKDAY_NAMES = ["Domingo", "Segunda-feira", "Terça-feira", "Quarta-feira", "Quinta-feira", "Sexta-feira", "Sábado"];

/**
 * Horários habituais deduzidos da agenda, para quando a igreja não os escreveu
 * no Público (Admin): cada dia e hora que se repete (ou que já está marcado
 * para a frente), ex.: «Domingo às 07:00».
 */
export const habitualScheduleLines = (services = []) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const slots = new Map();
  for (const s of services) {
    const date = parseLocalDate(s?.date);
    if (!date || /^resumo/i.test(String(s?.type || "").trim())) continue;
    const time = fmtTime(s.time);
    const key = `${date.getDay()}|${time}`;
    const slot = slots.get(key) || { weekday: date.getDay(), time, count: 0, upcoming: false };
    slot.count += 1;
    if (date >= today) slot.upcoming = true;
    slots.set(key, slot);
  }
  return [...slots.values()]
    .filter((s) => s.count >= 2 || s.upcoming)
    .sort((a, b) => a.weekday - b.weekday || a.time.localeCompare(b.time))
    .map((s) => (s.time ? `${WEEKDAY_NAMES[s.weekday]} às ${s.time}` : WEEKDAY_NAMES[s.weekday]));
};

export const serviceTypeLabel = (service) => {
  const stored = String(service?.type || "").trim();
  const match = String(service?.date || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return stored || "Culto";
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const weekdays = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
  const weekday = weekdays[date.getDay()];
  if (/^Domingo (Manhã|Tarde)$/i.test(stored) && date.getDay() !== 0) return weekday;
  return stored || "Culto";
};

const PUBLIC_ANNOUNCEMENT_RETENTION_DAYS = 45;

export const isRecentAnnouncement = (value) => {
  if (!value) return true;
  const time = new Date(value).getTime();
  if (Number.isNaN(time)) return false;
  return time >= Date.now() - PUBLIC_ANNOUNCEMENT_RETENTION_DAYS * 24 * 60 * 60 * 1000;
};

const sanitizeBase = (value) => (value ? value.replace(/\/$/, "") : "");

const PUBLIC_API_BASE = sanitizeBase(process.env.REACT_APP_PUBLIC_API_BASE || "/api/public");

const guessAssetsBase = () => {
  const envBase = sanitizeBase(process.env.REACT_APP_FILES_BASE_URL || process.env.REACT_APP_BACKEND_URL || "");
  if (envBase) return envBase;
  if (PUBLIC_API_BASE.startsWith("http")) {
    try {
      const url = new URL(PUBLIC_API_BASE);
      return url.origin;
    } catch {
      return "";
    }
  }
  return "";
};

const ASSETS_BASE = guessAssetsBase();

const RUNTIME_ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

export const buildEndpoint = (segment) => `${PUBLIC_API_BASE}${segment.startsWith("/") ? segment : `/${segment}`}`;

export function toAbsoluteUrl(maybeRelative) {
  const v = String(maybeRelative || "").trim();
  if (!v) return "";
  if (/^https?:\/\//i.test(v)) return v;
  const origin = ASSETS_BASE || RUNTIME_ORIGIN || (typeof window !== "undefined" ? window.location.origin : "");
  if (!origin) return v;
  return v.startsWith("/") ? `${origin}${v}` : `${origin}/${v}`;
}

export function mapsDirectionsUrl(directionsUrl, address) {
  const d = String(directionsUrl || "").trim();
  if (d && /^https?:\/\//i.test(d) && !/\/maps\/embed/i.test(d)) return d;
  const q = String(address || "").trim();
  if (!q) return "";
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
}

export function toGoogleEmbedUrl(raw) {
  if (!raw || typeof raw !== "string") return "";
  const input = raw.trim();
  if (!input) return "";

  // Already an embeddable URL
  if (/google\.com\/maps\/embed/i.test(input)) return input;

  // Fallbacks from common Google Maps links:
  // - https://maps.google.com/?q=...
  // - https://www.google.com/maps?q=...
  // - https://www.google.com/maps/place/...
  // - https://goo.gl/maps/... (kept as external, cannot reliably embed client-side)
  try {
    const url = new URL(input);
    const host = url.hostname.toLowerCase();
    const path = url.pathname || "";
    const q = url.searchParams.get("q");

    if (!host.includes("google.") && !host.includes("maps.")) return "";

    if (q && q.trim()) {
      return `https://www.google.com/maps?q=${encodeURIComponent(q.trim())}&output=embed`;
    }

    if (path.includes("/maps/place/")) {
      const placePart = path.split("/maps/place/")[1] || "";
      if (placePart) {
        const decoded = decodeURIComponent(placePart).replace(/\+/g, " ").trim();
        if (decoded) {
          return `https://www.google.com/maps?q=${encodeURIComponent(decoded)}&output=embed`;
        }
      }
    }

    if (path.includes("/maps")) {
      return `${url.origin}${url.pathname}${url.search ? url.search + "&" : "?"}output=embed`;
    }
  } catch {
    // Not a URL; treat as plain address text
  }

  // If user provided plain address, still try embed query.
  return `https://www.google.com/maps?q=${encodeURIComponent(input)}&output=embed`;
}

/** Se o «versículo» for só uma referência (ex. Mateus 28:19-20), trata como cite. */
export function normalizeVerse(verse, verseRef) {
  const v = String(verse || "").trim();
  const r = String(verseRef || "").trim();
  const looksLikeRef = (s) =>
    !!s &&
    s.length < 48 &&
    /\d+\s*:\s*\d+/.test(s) &&
    !/[.!?]/.test(s) &&
    s.split(/\s+/).length <= 6;
  if (looksLikeRef(v)) {
    return { text: "", ref: r && !looksLikeRef(r) ? r : v };
  }
  // Corrige o valor antigo que associava «Eu e a minha casa…» a Mateus.
  const correctedRef = /minha casa|serviremos ao senhor/i.test(v) && /^mateus\s+28\s*:/i.test(r)
    ? "Josué 24:15"
    : r;
  return { text: v, ref: correctedRef };
}

export function digitsOnly(raw) {
  const fromUrl = String(raw || "").match(/wa\.me\/(\d+)/i);
  if (fromUrl) return fromUrl[1];
  return String(raw || "").replace(/\D/g, "");
}

/** Formata telefone MZ para exibição: +258 82 631 9370 */
export function formatMzPhone(raw) {
  let d = digitsOnly(raw);
  if (!d) return String(raw || "").trim();
  if (d.length === 9) d = `258${d}`;
  if (d.startsWith("258") && d.length >= 12) {
    return `+${d.slice(0, 3)} ${d.slice(3, 5)} ${d.slice(5, 8)} ${d.slice(8, 12)}${d.length > 12 ? ` ${d.slice(12)}` : ""}`;
  }
  return String(raw || "").trim();
}

export function toWhatsAppHref(phoneOrLink, customLink = "") {
  if (customLink && /^https?:\/\//i.test(customLink)) return customLink;
  if (/^https?:\/\//i.test(String(phoneOrLink || ""))) return phoneOrLink;
  let d = digitsOnly(phoneOrLink || customLink);
  if (!d) return "";
  if (d.length === 9) d = `258${d}`;
  return `https://wa.me/${d}`;
}

export function prayerHref(prayerUrl, whatsappHref) {
  const rawPrayerUrl = String(prayerUrl || "").trim();
  if (/^https?:\/\//i.test(rawPrayerUrl)) return rawPrayerUrl;
  if (rawPrayerUrl) {
    const normalizedPrayerUrl = toWhatsAppHref(rawPrayerUrl);
    if (normalizedPrayerUrl) {
      const sep = normalizedPrayerUrl.includes("?") ? "&" : "?";
      return `${normalizedPrayerUrl}${sep}text=${encodeURIComponent("Paz do Senhor. Gostaria de pedir oração.")}`;
    }
  }
  if (!whatsappHref) return "#";
  const sep = whatsappHref.includes("?") ? "&" : "?";
  return `${whatsappHref}${sep}text=${encodeURIComponent("Paz do Senhor. Gostaria de pedir oração.")}`;
}

export function cleanMediaCaption(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  // captions de sistema / metadados internos não devem aparecer no alt
  if (/\[hero:\d+\]|\[at:|\[alt:\d+\]/i.test(s)) return "";
  return s;
}

export function isLikelyBrokenUploadUrl(url) {
  // Só filtramos placeholders internos; uploads reais são validados pelo próprio <img>.
  return /^\[missing\]|^$/.test(String(url || "").trim());
}
